var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });

// src/components/BasePage/BasePage.tsx
import { KeyboardArrowUp as KeyboardArrowUpIcon } from "@mui/icons-material";
import { Container, Fab, Typography } from "@mui/material";
import { CookieConsent } from "@xylabs/react-cookie-consent";
import { FlexCol, FlexRow } from "@xylabs/react-flexbox";
import { ScrollToTop, ScrollToTopButton } from "@xylabs/react-scroll-to-top";
import React2, { use } from "react";
import { Helmet } from "react-helmet";

// src/contexts/LoadStatus/Context.ts
import { createContext } from "react";
var LoadStatusContext = createContext({
  status: "done",
  setStatus: /* @__PURE__ */ __name(() => {
  }, "setStatus"),
  setError: /* @__PURE__ */ __name(() => {
  }, "setError")
});

// src/contexts/LoadStatus/Provider.tsx
import React, { useEffect, useMemo, useState } from "react";
var LoadStatusProvider = /* @__PURE__ */ __name(({ timeout = 5e3, children }) => {
  useEffect(() => {
    if (timeout !== 0 && timeout !== Infinity) {
      const id = setTimeout(() => {
        setStatus("error");
        setError(new Error("Timeout after " + timeout + "ms"));
      }, timeout);
      return () => {
        clearTimeout(id);
      };
    }
  }, [
    timeout
  ]);
  const [error, setError] = useState();
  const [status, setStatus] = useState("loading");
  const value = useMemo(() => {
    return {
      status,
      setStatus,
      setError,
      error
    };
  }, [
    status,
    setStatus,
    error,
    setError
  ]);
  return /* @__PURE__ */ React.createElement(LoadStatusContext, {
    value
  }, children);
}, "LoadStatusProvider");

// src/components/BasePage/BasePage.tsx
var xyoOgMetaName = "xyo:og:image";
var BasePage = /* @__PURE__ */ __name(({ disableGutters, children, beta, container, hideFooter, appFooter, appBar, cookieConsent, description, hideAppBar, metaServer, noindex = false, scrollToTopButton = false, title: titleProp, ...props }) => {
  const { status, setStatus } = use(LoadStatusContext);
  const scrollToTopAnchorId = "scroll-to-top-anchor";
  const { pageCompleteMetaName = xyoOgMetaName, title = titleProp, shareImage, shareMode = "static" } = metaServer ?? {};
  if (shareMode === "static" && status !== "done") {
    setStatus("done");
  } else if (xyoOgMetaName === pageCompleteMetaName && shareImage && status !== "done") {
    setStatus("done");
  }
  return /* @__PURE__ */ React2.createElement(FlexCol, {
    justifyContent: "flex-start",
    alignItems: "stretch",
    width: "100%",
    maxWidth: "100vw",
    minHeight: "100vh",
    ...props
  }, /* @__PURE__ */ React2.createElement(ScrollToTop, null), /* @__PURE__ */ React2.createElement(Helmet, {
    title
  }, noindex ? /* @__PURE__ */ React2.createElement("meta", {
    content: "noindex",
    name: "robots"
  }) : null, /* @__PURE__ */ React2.createElement("meta", {
    property: "og:title",
    content: title
  }), /* @__PURE__ */ React2.createElement("meta", {
    property: "og:description",
    content: description ?? title
  })), scrollToTopButton ? /* @__PURE__ */ React2.createElement("div", {
    id: scrollToTopAnchorId
  }) : null, hideAppBar ? null : appBar, beta ? /* @__PURE__ */ React2.createElement(FlexRow, {
    margin: 1,
    position: "absolute",
    top: 0,
    left: 0,
    bgcolor: "#cccccc88",
    paddingX: 1,
    style: {
      opacity: 0.5
    }
  }, /* @__PURE__ */ React2.createElement(Typography, {
    variant: "body2"
  }, "Important: This page is a Beta page. It is possible that some information may not be correct.")) : null, container ? /* @__PURE__ */ React2.createElement(Container, {
    style: {
      alignItems: "stretch",
      display: "flex",
      flexDirection: "column",
      flexGrow: 1
    },
    maxWidth: container,
    disableGutters
  }, children) : children, hideFooter ? null : /* @__PURE__ */ React2.createElement("footer", null, appFooter), cookieConsent ?? /* @__PURE__ */ React2.createElement(CookieConsent, {
    borderRadius: 0
  }), scrollToTopButton ? /* @__PURE__ */ React2.createElement(ScrollToTopButton, {
    anchorId: scrollToTopAnchorId
  }, /* @__PURE__ */ React2.createElement(Fab, {
    "aria-label": "scroll to top",
    color: "secondary",
    size: "small"
  }, /* @__PURE__ */ React2.createElement(KeyboardArrowUpIcon, null))) : null, /* @__PURE__ */ React2.createElement(Helmet, null, /* @__PURE__ */ React2.createElement("meta", {
    property: "xy:meta:share:mode",
    content: shareMode
  }), shareImage ? /* @__PURE__ */ React2.createElement("meta", {
    property: xyoOgMetaName,
    content: shareImage
  }) : null, xyoOgMetaName === pageCompleteMetaName && shareImage ? null : /* @__PURE__ */ React2.createElement("meta", {
    property: pageCompleteMetaName,
    content: ""
  }), /* @__PURE__ */ React2.createElement("meta", {
    property: "xy:meta:status",
    content: status ?? "done"
  })));
}, "BasePage");

// src/components/BasePage/DynamicSharePage.tsx
import React3 from "react";
var DynamicSharePage = /* @__PURE__ */ __name(({ metaServer, ...props }) => {
  return /* @__PURE__ */ React3.createElement(BasePage, {
    ...props,
    metaServer: {
      ...metaServer,
      shareMode: "dynamic"
    }
  });
}, "DynamicSharePage");

// src/components/BasePage/LiveSharePage.tsx
import React4 from "react";
var LiveSharePage = /* @__PURE__ */ __name(({ metaServer, ...props }) => {
  return /* @__PURE__ */ React4.createElement(BasePage, {
    ...props,
    metaServer: {
      ...metaServer,
      shareMode: "live"
    }
  });
}, "LiveSharePage");

// src/components/BasePage/PermaSharePage.tsx
import React5 from "react";
var PermaSharePage = /* @__PURE__ */ __name(({ metaServer, ...props }) => {
  return /* @__PURE__ */ React5.createElement(BasePage, {
    ...props,
    metaServer: {
      ...metaServer,
      shareMode: "permanent"
    }
  });
}, "PermaSharePage");
export {
  BasePage,
  DynamicSharePage,
  LiveSharePage,
  LoadStatusContext,
  LoadStatusProvider,
  PermaSharePage
};
//# sourceMappingURL=index.mjs.map